import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Button, Form, FormGroup, Label } from "reactstrap";
import api, { baseURL } from "../../Axios";
import Swal from "sweetalert2";

const UpdateHouseImages = () => {
  const history = useHistory();
  const { id } = useParams();

  const [existingImages, setExistingImages] = useState({
    housePic1: null,
    housePic2: null,
    housePic3: null,
    housePic4: null,
  });

  const [newImages, setNewImages] = useState({
    housePic1: null,
    housePic2: null,
    housePic3: null,
    housePic4: null,
  });

  const [previews, setPreviews] = useState({
    housePic1: null,
    housePic2: null,
    housePic3: null,
    housePic4: null,
  });

  const [deletedImages, setDeletedImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const labelStyle = {
    color: "#212121",
    fontWeight: "800",
    marginBottom: "6px",
  };

  const fetchImages = () => {
    api
      .get(`/admin/userProfile/profileView?userId=${id}`)
      .then((res) => {
        const d = res.data && res.data.data ? res.data.data : {};
        const docs = d.docs || {};
        setExistingImages({
          housePic1: docs.housePic1 || null,
          housePic2: docs.housePic2 || null,
          housePic3: docs.housePic3 || null,
          housePic4: docs.housePic4 || null,
        });
      })
      .catch(() => {
        Swal.fire("Error", "Failed to load house images", "error");
      });
  };

  useEffect(() => {
    fetchImages();
  }, [id]);

  const handleImageChange = (e, imageKey) => {
    const file = e.target.files[0];
    if (file) {
      setNewImages((prev) => ({ ...prev, [imageKey]: file }));
      setPreviews((prev) => ({ ...prev, [imageKey]: URL.createObjectURL(file) }));
      setDeletedImages((prev) => prev.filter((key) => key !== imageKey));
    }
  };

  const handleDeleteImage = (imageKey) => {
    Swal.fire({
      title: "Delete Image?",
      text: "Are you sure you want to delete this image?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setDeletedImages((prev) => [...prev, imageKey]);
        setExistingImages((prev) => ({ ...prev, [imageKey]: null }));
        setPreviews((prev) => ({ ...prev, [imageKey]: null }));
        setNewImages((prev) => ({ ...prev, [imageKey]: null }));
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("userId", id);

    if (newImages.housePic1) formData.append("housePic1", newImages.housePic1);
    if (newImages.housePic2) formData.append("housePic2", newImages.housePic2);
    if (newImages.housePic3) formData.append("housePic3", newImages.housePic3);
    if (newImages.housePic4) formData.append("housePic4", newImages.housePic4);

    if (deletedImages.length > 0) {
      formData.append("deletedImages", JSON.stringify(deletedImages));
    }

    try {
      await api.post("/admin/userProfile/houseImageUpdate/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      Swal.fire("Success", "House images updated successfully", "success").then(() => {
        history.goBack();
      });
    } catch (err) {
      Swal.fire("Error", "Failed to update house images", "error");
    } finally {
      setLoading(false);
    }
  };

  const imageStyle = {
    width: "200px",
    height: "150px",
    objectFit: "cover",
    borderRadius: "12px",
    border: "1.5px solid #757575",
  };

  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    padding: "20px",
    border: "1.5px solid #757575",
    borderRadius: "12px",
    backgroundColor: "#fafafa",
    position: "relative",
  };

  const deleteButtonStyle = {
    position: "absolute",
    top: "10px",
    right: "10px",
    background: "#ff4444",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "30px",
    height: "30px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const renderImageBox = (imageKey, label) => {
    const hasImage = previews[imageKey] || existingImages[imageKey];
    const isDeleted = deletedImages.includes(imageKey);

    return (
      <FormGroup style={containerStyle}>
        <Label style={labelStyle}>{label}</Label>
        {hasImage && !isDeleted ? (
          <>
            <img
              src={previews[imageKey] || baseURL + existingImages[imageKey]}
              alt={label}
              style={imageStyle}
            />
            <button
              type="button"
              style={deleteButtonStyle}
              onClick={() => handleDeleteImage(imageKey)}
              title="Delete Image"
            >
              ×
            </button>
          </>
        ) : (
          <div
            style={{
              ...imageStyle,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f0f0f0",
              color: "#999",
            }}
          >
            No Image
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          onChange={(e) => handleImageChange(e, imageKey)}
          style={{ marginTop: "10px" }}
        />
      </FormGroup>
    );
  };

  return (
    <div style={{ background: "#fff", marginTop: "70px" }}>
      <div
        style={{
          color: "#1a237e",
          fontWeight: "900",
          padding: "20px 10px",
        }}
      >
        <h4 style={{ fontSize: "1.8rem" }}>Update House Images</h4>
      </div>

      <Form
        onSubmit={handleSubmit}
        style={{ padding: 24, maxWidth: 1200, margin: "0 10px" }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "20px",
          }}
        >
          {renderImageBox("housePic1", "House Image 1")}
          {renderImageBox("housePic2", "House Image 2")}
          {renderImageBox("housePic3", "House Image 3")}
          {renderImageBox("housePic4", "House Image 4")}
        </div>

        <Button
          type="submit"
          disabled={loading}
          style={{
            width: "30%",
            borderRadius: 8,
            backgroundColor: "#000",
            color: "#fff",
            fontWeight: "bold",
            padding: "10px 0",
            marginTop: "30px",
          }}
        >
          {loading ? "Uploading..." : "Save & Continue"}
        </Button>
      </Form>
    </div>
  );
};

export default UpdateHouseImages;
